import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { API_URL } from "./config";
import { useParams } from "react-router-dom";

const AILessonAnalyticsPage = () => {
  const { lessonId } = useParams();
  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const BLUE = "#1565C0";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${API_URL}/api/ai-lesson-delivery/teacher/lessons/${lessonId}/analytics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPayload(res.data || null);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [lessonId, token]);

  const lesson = payload?.lesson || {};
  const summary = payload?.summary || {};
  const statusBreakdown = payload?.statusBreakdown || [];
  const questionBreakdown = payload?.questionBreakdown || [];

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const cardSx = {
    p: 2,
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
    height: "100%",
  };

  return (
    <Box sx={{ p: 2, backgroundColor: BG, minHeight: "100vh" }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
          }}
        >
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
            AI Lesson Analytics
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
            Performance snapshot and question-level analysis
          </Typography>
        </Box>

        <Box sx={{ p: 2, backgroundColor: BG }}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Paper elevation={0} sx={{ ...cardSx }}>
              <Typography>Loading analytics...</Typography>
            </Paper>
          ) : !payload ? (
            <Paper elevation={0} sx={{ ...cardSx }}>
              <Typography>No analytics found.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ ...cardSx }}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.15rem", mb: 1 }}>
                    {lesson.LessonTitle}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip size="small" label={`Subject: ${lesson.SubjectCode || "-"}`} />
                    <Chip size="small" label={`Chapter: ${lesson.ChapterTitle || "-"}`} />
                    <Chip size="small" label={`ClassID: ${lesson.ClassID || "-"}`} />
                    <Chip size="small" label={`Branch: ${lesson.BranchCode || "-"}`} />
                    <Chip size="small" label={`Semester: ${lesson.Semester || "-"}`} />
                    <Chip size="small" label={`Section: ${lesson.Section || "-"}`} />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>Unique Students</Typography>
                  <Typography sx={{ fontSize: "1.8rem", fontWeight: 900 }}>
                    {summary.UniqueStudents || 0}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>Avg Completion</Typography>
                  <Typography sx={{ fontSize: "1.8rem", fontWeight: 900 }}>
                    {summary.AvgCompletionPercent || 0}%
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>Avg Total Score</Typography>
                  <Typography sx={{ fontSize: "1.8rem", fontWeight: 900 }}>
                    {summary.AvgTotalScore || 0}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>Avg Time Spent</Typography>
                  <Typography sx={{ fontSize: "1.4rem", fontWeight: 900 }}>
                    {summary.AvgTimeSpentSeconds || 0}s
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                    Status Breakdown
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>Status</TableCell>
                          <TableCell>Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statusBreakdown.map((row, idx) => (
                          <TableRow key={row.Status} sx={zebraRowSx(idx)}>
                            <TableCell>{row.Status}</TableCell>
                            <TableCell>{row.CountValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                    Summary
                  </Typography>

                  <Typography><b>Total Attempts:</b> {summary.TotalAttempts || 0}</Typography>
                  <Typography><b>Not Started:</b> {summary.NotStartedCount || 0}</Typography>
                  <Typography><b>In Progress:</b> {summary.InProgressCount || 0}</Typography>
                  <Typography><b>Submitted:</b> {summary.SubmittedCount || 0}</Typography>
                  <Typography><b>Graded:</b> {summary.GradedCount || 0}</Typography>
                  <Typography><b>Avg Objective Score:</b> {summary.AvgObjectiveScore || 0}</Typography>
                  <Typography><b>Avg Subjective Score:</b> {summary.AvgSubjectiveScore || 0}</Typography>
                  <Typography><b>Avg Max Score:</b> {summary.AvgMaxScore || 0}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} sx={cardSx}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, mb: 2 }}>
                    Question-Level Breakdown
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>QuestionId</TableCell>
                          <TableCell>Question</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Responses</TableCell>
                          <TableCell>Avg Final</TableCell>
                          <TableCell>Avg Max</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {questionBreakdown.map((row, idx) => (
                          <TableRow key={row.QuestionId} sx={zebraRowSx(idx)}>
                            <TableCell>{row.QuestionId}</TableCell>
                            <TableCell>{row.QuestionText}</TableCell>
                            <TableCell>{row.QuestionType}</TableCell>
                            <TableCell>{row.Responses}</TableCell>
                            <TableCell>{row.AvgFinalScore}</TableCell>
                            <TableCell>{row.AvgMaxScore}</TableCell>
                          </TableRow>
                        ))}
                        {!questionBreakdown.length && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography color="text.secondary">No question analytics yet.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AILessonAnalyticsPage;